import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { NotifService } from '../../../services/notifs/notif.service';
import { NotifType } from '../../models/notif.model';
import { NotifsUtils } from '../../../utils/notifs/notifs.utils';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { map, take } from 'rxjs/operators';
import { LanguageService } from "../../lang/language.service";

type AuthGuardType =
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree;

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  
  public static REDIRECT_URL = 'redirect_url';
  
  constructor(
    private readonly languageService: LanguageService,
    private readonly notifService: NotifService,
    private readonly authService: AuthService
  ) {
  }
  
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): AuthGuardType {
    if (this.automaticallyRedirectWhenComesFromMail(route)) {
      return true;
    }
    
    return this.authService.isAuthenticated$.pipe(
      take(1),
      map(isAuthenticated => {
        if (!isAuthenticated) {
          return this.parseLoginUrl(state.url);
        }
        return true;
      })
    );
  }
  
  canLoad(route: Route, segments: UrlSegment[]): AuthGuardType {
    const fullPath = segments.reduce(
      (path, currentSegment) =>
        `${path}/${currentSegment.path}${window.location.search}`,
      ''
    );
    
    return this.authService.isAuthenticated$.pipe(
      take(1),
      map(isAuthenticated => {
        if (!isAuthenticated) {
          return this.parseLoginUrl(fullPath);
        }
        return true;
      })
    );
  }
  
  /**
   *
   * @param redirectUrl Redirects to login with original URL request as params
   */
  private parseLoginUrl(redirectUrl: string): boolean {
    const codec = new HttpUrlEncodingCodec();
    const encodedUrl = codec.encodeValue(redirectUrl);
    this.login(encodedUrl);
    return false;
  }
  
  private automaticallyRedirectWhenComesFromMail(
    route: ActivatedRouteSnapshot
  ): boolean {
    const notifType = route.queryParamMap.get('notif');
    const redirectId = route.queryParamMap.get('redirect_id');
    const misc = route.queryParamMap.get('misc');
    if (notifType && redirectId) {
      const type = NotifType[notifType as keyof typeof NotifType];
      if (type) {
        const url = NotifsUtils.getUrl(redirectId, type, misc);
        this.notifService.navigate(url);
        return true;
      }
    }
    return false;
  }
  
  private login(redirectUrl: string): void {
    this.authService.loginWithRedirect({
      appState: { target: redirectUrl, justLoggedIn: true },
      authorizationParams: {
        ui_locales: this.languageService.currentLanguage,
        redirect_uri: window.location.origin,
        'ext-signup_url': `${window.location.origin}/onboarding`,
      }
    });
  }
}
